import { useState } from "react";
import type {
  AnfrageRohdaten,
  DateiRef,
  PdfTyp,
} from "@bidi-handel/core/schemas";
import { api, ApiError } from "../../api/client";

/**
 * PDF-Klassifikations-Box (Sprint G6a) — Redesign-Look.
 *
 * Zeigt für jede hochgeladene PDF die beim Upload ermittelte Klassifikation
 * und erlaubt User-Korrektur. Sobald alle PDFs bestaetigt sind, verschwindet
 * die Box.
 */

const TYP_LABEL: Record<PdfTyp, string> = {
  lv: "Leistungsverzeichnis",
  bauplan: "Bauplan",
  unklar: "Unklar",
};

const TYP_TONE: Record<PdfTyp, "ok" | "acc" | "warn"> = {
  lv: "ok",
  bauplan: "acc",
  unklar: "warn",
};

export function PdfKlassifikationBox({
  rohdaten,
  tenantId,
  onChanged,
}: {
  rohdaten: AnfrageRohdaten;
  tenantId: string;
  onChanged: () => void;
}) {
  const pdfDateien = rohdaten.dateien.filter(
    (d) => d.relPath.toLowerCase().endsWith(".pdf") && d.pdfKlassifikation
  );
  if (pdfDateien.length === 0) return null;

  const alleBestaetigt = pdfDateien.every((d) => d.pdfKlassifikation?.bestaetigt);
  if (alleBestaetigt) return null;

  return (
    <section className="block" style={{ borderColor: "oklch(0.82 0.15 75 / 0.4)" }}>
      <div className="block-head">
        <span className="uppercase-mini" style={{ color: "var(--warn)" }}>
          // PDF-KLASSIFIKATION BESTÄTIGEN
        </span>
        <span className="block-sep" />
        <span className="uppercase-mini" style={{ color: "var(--fg-faint)" }}>
          Pipeline startet erst nach Bestätigung
        </span>
      </div>
      <div style={{ display: "grid", gap: 10 }}>
        {pdfDateien.map((d) => (
          <PdfRow
            key={d.relPath}
            datei={d}
            tenantId={tenantId}
            anfrageId={rohdaten.anfrageId}
            onChanged={onChanged}
          />
        ))}
      </div>
    </section>
  );
}

function PdfRow({
  datei,
  tenantId,
  anfrageId,
  onChanged,
}: {
  datei: DateiRef;
  tenantId: string;
  anfrageId: string;
  onChanged: () => void;
}) {
  const k = datei.pdfKlassifikation!;
  const [saving, setSaving] = useState<PdfTyp | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileName = datei.relPath.split("/").pop() ?? datei.relPath;

  async function setKlassifikation(neu: PdfTyp) {
    setSaving(neu);
    setError(null);
    try {
      await api.patchDateiKlassifikation(tenantId, anfrageId, {
        relPath: datei.relPath,
        erkanntAls: neu,
        bestaetigt: true,
      });
      onChanged();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { hint?: string; error?: string } | null;
        setError(body?.hint ?? body?.error ?? err.message);
      } else if (err instanceof Error) setError(err.message);
      else setError(String(err));
    } finally {
      setSaving(null);
    }
  }

  return (
    <div
      style={{
        padding: "14px 16px",
        background: "var(--bg-raised)",
        border: "1px solid var(--line-soft)",
        borderRadius: "var(--r)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 14, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="source-name mono">{fileName}</div>
          <div
            style={{
              marginTop: 6,
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span style={{ color: "var(--fg-mute)", fontSize: 13 }}>Erkannt als:</span>
            <span className={`pill ${TYP_TONE[k.erkanntAls]}`}>
              <span className="dot" />
              {TYP_LABEL[k.erkanntAls].toUpperCase()}
            </span>
            <span className="mono" style={{ color: "var(--fg-faint)", fontSize: 11 }}>
              {k.confidence}% sicher
            </span>
          </div>
          {k.hinweise.length > 0 && (
            <ul
              style={{
                marginTop: 8,
                paddingLeft: 0,
                listStyle: "none",
                fontSize: 11,
                color: "var(--fg-dim)",
                display: "grid",
                gap: 2,
              }}
            >
              {k.hinweise.slice(0, 3).map((h, i) => (
                <li key={i}>· {h}</li>
              ))}
              {k.hinweise.length > 3 && (
                <li style={{ fontStyle: "italic" }}>… +{k.hinweise.length - 3} weitere</li>
              )}
            </ul>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(["lv", "bauplan", "unklar"] as const).map((typ) => (
              <button
                key={typ}
                type="button"
                disabled={saving !== null}
                onClick={() => setKlassifikation(typ)}
                className={`btn sm ${k.erkanntAls === typ ? "primary" : ""}`}
              >
                {saving === typ ? "…" : k.erkanntAls === typ ? `${TYP_LABEL[typ]} ✓` : TYP_LABEL[typ]}
              </button>
            ))}
          </div>
        </div>
      </div>
      {error && (
        <p
          className="mono"
          style={{ marginTop: 8, fontSize: 11, color: "var(--bad)" }}
        >
          {error}
        </p>
      )}
    </div>
  );
}
